import { SWRFetcher } from '@commenda/utils';
import useSWR from 'swr';
import {
  GetFilingFrequencyChangeLogsByCorporationAndStateResponse,
  ListFilingsParams,
  SalesTaxFiling,
} from './types';

const EMPTY_FILINGS: SalesTaxFiling[] = [];

export function useFilingsList(
  params: ListFilingsParams & { enabled?: boolean }
) {
  const { enabled = true, ...restParams } = params;
  const queryParams = new URLSearchParams();

  if (restParams.corporationId)
    queryParams.append('corporationId', restParams.corporationId.toString());
  if (restParams.cursor) queryParams.append('cursor', restParams.cursor);
  if (restParams.limit)
    queryParams.append('limit', restParams.limit.toString());
  if (restParams.taxPeriodEndDate)
    queryParams.append('taxPeriodEndDate', restParams.taxPeriodEndDate);
  if (restParams.status)
    queryParams.append('status', restParams.status.join(','));
  if (restParams.frequency)
    queryParams.append('frequency', restParams.frequency.join(','));
  if (restParams.jurisdiction)
    queryParams.append('jurisdiction', restParams.jurisdiction.join(','));
  if (restParams.includeServiceRequest)
    queryParams.append('includeServiceRequest', 'true');
  if (restParams.includeAgent) queryParams.append('includeAgent', 'true');
  if (restParams.includeAssignee) queryParams.append('includeAssignee', 'true');

  const swrKey = enabled
    ? `new-sales-tax/filings?${queryParams.toString()}`
    : null;

  const { data, error, isLoading, mutate } = useSWR<{
    data: {
      filings: SalesTaxFiling[];
      nextCursor: string | null;
      total: number;
    };
  }>(swrKey, SWRFetcher({ method: 'GET' }, undefined));

  return {
    filings: data?.data.filings ?? EMPTY_FILINGS,
    nextCursor: data?.data.nextCursor,
    total: data?.data.total || 0,
    isLoading: enabled ? isLoading : false,
    error,
    mutate,
  };
}

export function useFilingFrequencyChangesByState(params: {
  corporationId: number;
  state: string;
}) {
  const queryParams = new URLSearchParams();

  queryParams.append('corporationId', params.corporationId.toString());
  queryParams.append('state', params.state);

  const { data, error, isLoading, mutate } =
    useSWR<GetFilingFrequencyChangeLogsByCorporationAndStateResponse>(
      `new-sales-tax/filings/frequency-change-logs?${queryParams.toString()}`,
      SWRFetcher({ method: 'GET' }, undefined)
    );

  return {
    frequencyChangeLogs: data?.data.changes || [],
    nextCursor: data?.data.cursor,
    total: data?.data.total_changes || 0,
    isLoading,
    error,
    mutate,
  };
}
